/**
 * SubscriptionGate - Shared paywall component for all Quantrolix apps.
 *
 * Copy this file into your app's src/components/ directory.
 * Requires: React 18+, fetch API
 *
 * Usage:
 *   import SubscriptionGate from './components/SubscriptionGate';
 *   function App() {
 *     return (
 *       <SubscriptionGate appId="fire-pos" authUrl="https://apps.quantrolix.com/api/auth">
 *         <YourAppContent />
 *       </SubscriptionGate>
 *     );
 *   }
 *
 * Ticket 4556: EmberPOS's native (Electron MAS + Capacitor iOS) builds used
 * to render the app unconditionally (`isNativeApp` early-returns below,
 * removed) — required back when Apple builds shipped free (Guideline
 * 3.1.1). LOCKED DECISIONS now require a real paywall everywhere: this file
 * gates native builds on the SAME tenant entitlement web uses
 * (tenants.subscription_tier — written by Stripe OR by a verified Apple
 * purchase, see src/services/appStorePurchase.js + server/routes/
 * applePurchases.js), with a StoreKit-native paywall UI instead of Stripe.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createContext, useContext } from 'react';
import { isNativeApp, API_BASE_URL } from '../config';
import RendererReadyShell from './RendererReadyShell';
import PostSignupSetup from './onboarding/PostSignupSteps';
import { canStartTrial } from '../utils/trialGuard';
import { getDeviceToken, setDeviceToken, clearDeviceToken } from '../lib/authToken';
import {
  getAppStoreProducts,
  purchaseAppStorePlan,
  restoreAppStorePurchases,
  setCachedTier,
  setCachedLocationLimit,
  watchPendingTransactions,
} from '../services/appStorePurchase';

const AUTH_URL = 'https://apps.quantrolix.ai/api/auth';

const SubscriptionContext = createContext({
  user: null,
  tenant: null,
  subscription: null,
  isLoading: true,
  isAuthenticated: false,
  isSubscribed: false,
  isTrial: false,
  trialDaysLeft: 0,
  isPremium: false,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  subscribe: async () => {},
  purchasePremium: async () => {},
  restorePremium: async () => {},
});

export function useSubscription() {
  return useContext(SubscriptionContext);
}

// A tenant has app access when ANY of: an active/trialing paid tier (Stripe
// OR Apple — same columns, server/routes/applePurchases.js writes the same
// ones payments.js's Stripe webhook does), the account's own server-issued
// trial window (tenants.trial_ends_at, set at registration), or it predates
// the Apple-IAP-paywall cutover (isGrandfathered — server-computed, ticket
// 4556 LOCKED DECISIONS: "never a client-side check", see
// server/config/appleIapCutover.js). Never re-derive any of this from
// client-cached data; `tenant` here always comes straight off /me, /login,
// or /register.
function hasPaidTier(tenant) {
  return !!tenant?.subscriptionTier && tenant.subscriptionTier !== 'free'
    && ['active', 'trialing'].includes(tenant.subscriptionStatus);
}

// Apple purchases have no server-side renewal webhook wired up here (ticket
// 4556) — unlike Stripe (server/routes/payments.js flips subscription_status
// to 'canceled' the moment a subscription ends), an Apple subscription's
// status can go stale between launches instead of flipping the moment it
// lapses. subscription_ends_at is the authoritative signal for those:
// tenant.appleProductId marks a row as Apple-sourced (server/routes/
// tenants.js's /me), and a past subscription_ends_at on one means
// unentitled even though subscription_status still reads 'active'/'trialing'.
function appleSubscriptionExpired(tenant) {
  return !!tenant?.appleProductId
    && !!tenant.subscriptionEndsAt
    && new Date(tenant.subscriptionEndsAt).getTime() <= Date.now();
}

function tenantHasAccess(tenant) {
  if (!tenant) return false;
  if (tenant.isGrandfathered) return true;
  if (hasPaidTier(tenant) && !appleSubscriptionExpired(tenant)) return true;
  if (tenant.trialEndsAt && new Date(tenant.trialEndsAt).getTime() > Date.now()) return true;
  return false;
}

// ─── Storage Helpers ────────────────────────────────────────────────────────

// The owner JWT this gate obtains is the device token (lib/authToken.js) —
// the same one PIN login sends to pick this device's restaurant.
function clearStoredToken(appId) {
  clearDeviceToken();
  try {
    localStorage.removeItem(`${appId}_trial_start`);
  } catch { /* localStorage unavailable */ }
}

function setTrialStart(appId, token) {
  try {
    // ponytail: single choke point — a trial may only be minted when backed
    // by a server-issued token, or clearing localStorage yields an
    // unlimited free trial (the tokenless-trial bug).
    if (!canStartTrial(token)) return;
    if (!localStorage.getItem(`${appId}_trial_start`)) {
      localStorage.setItem(`${appId}_trial_start`, new Date().toISOString());
    }
  } catch { /* localStorage unavailable */ }
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function SubscriptionGate({ appId, pricing, authUrl = AUTH_URL, children }) {
  const baseUrl = authUrl || AUTH_URL;
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [screen, setScreen] = useState('loading'); // loading | login | paywall | app
  const [error, setError] = useState('');
  // T4: set by a successful register only. Holds the owner's starter PIN
  // (starterStaff.ownerPin) in memory, never persisted; while set, the
  // post-sign-up steps (PIN once, then "Set up your menu") render before the
  // normal app/paywall screen.
  const [postSignup, setPostSignup] = useState(null);

  const trialDays = pricing?.trialDays || 7;

  // Last tenant this session's checkAuth confirmed access for, straight off
  // the server. A transient network error keeps a session alive on this
  // instead of bouncing an already-entitled tenant to login — but a network
  // error never mints a NEW grant (see checkAuth's catch below).
  const verifiedTenantRef = useRef(null);

  // isTrial/trialDaysLeft are read straight off the server-issued tenant
  // (tenant.trialEndsAt) — never off localStorage. Trial eligibility is
  // tenant-scoped and server-authoritative (ticket 4556 remediation):
  // clearing `${appId}_trial_start` has no effect on either value anymore.
  const isTrial = tenantHasAccess(tenant) && !tenant.isGrandfathered && !hasPaidTier(tenant);
  const trialDaysLeft = tenant?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(tenant.trialEndsAt).getTime() - Date.now()) / 86400000))
    : 0;

  // Records the server-issued trial start locally for the pre-account grace
  // window in checkAuth below. setTrialStart's canStartTrial guard is the
  // single choke point that keeps this from ever minting a trial without a
  // real server-issued token.
  const startTrial = useCallback((token) => setTrialStart(appId, token), [appId]);

  // ─── Native App Store purchase bridge ───────────────────────────────

  // Merges a grantToTenant() entitlement (server/routes/applePurchases.js:
  // { tier, status, periodEnd, productId, locationLimit }) into `tenant`
  // state using the SAME field names tenantHasAccess()/appleSubscriptionExpired()
  // read off /me's tenant (subscriptionEndsAt, appleProductId) — ticket 4556
  // finding: the merge used to drop periodEnd/productId, so after a
  // successful renewal of an EXPIRED sub the client kept computing against
  // the tenant's STALE (still-expired) subscriptionEndsAt and stayed gated
  // until an unrelated /me refreshed it. One helper, one place to keep the
  // three refresh points (purchase, restore, renewal push) from drifting
  // apart again.
  const mergeEntitlement = useCallback((entitlement) => {
    setTenant((prev) => ({
      ...(prev || {}),
      subscriptionTier: entitlement.tier,
      subscriptionStatus: entitlement.status,
      subscriptionEndsAt: entitlement.periodEnd,
      appleProductId: entitlement.productId,
      locationLimit: entitlement.locationLimit,
    }));
    setCachedTier(entitlement.tier);
    setCachedLocationLimit(entitlement.locationLimit);
  }, []);

  const purchasePremium = useCallback(async (productId) => {
    const entitlement = await purchaseAppStorePlan(productId);
    mergeEntitlement(entitlement);
    setScreen('app');
    return entitlement;
  }, [mergeEntitlement]);

  const restorePremium = useCallback(async () => {
    const entitlement = await restoreAppStorePurchases();
    mergeEntitlement(entitlement);
    setScreen('app');
    return entitlement;
  }, [mergeEntitlement]);

  // Electron MAS only: claim renewal transactions StoreKit delivers outside
  // an active purchase/restore call (electron/main.cjs's
  // 'app-store:pending-transactions' push) so a background renewal actually
  // refreshes the tenant's tier instead of sitting unclaimed. No-op elsewhere.
  // Only for a signed-in user (the claim is an authenticated server call), and
  // claimNow pulls what is already queued: main.cjs sends its launch push
  // before the window exists, and an unfinished transaction from an earlier
  // account on this Apple ID (which blocks buying that plan) is only cleared by
  // the account that signs in next. A null entitlement never changes the tier.
  const isAuthenticated = !!user;
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return watchPendingTransactions((entitlement) => {
      if (!entitlement) return;
      mergeEntitlement(entitlement);
    }, { claimNow: true });
  }, [isAuthenticated, mergeEntitlement]);

  // Whatever refreshed the tenant (purchase, restore, or a pending-transaction
  // claim after the purchase call already gave up), a paywall is never right
  // for a tenant that now has paid/grandfathered access (an in-trial tenant may
  // open Upgrade on purpose, so isTrial stays on the paywall). TestFlight 1.1.10: the buy completed
  // after the purchase() promise timed out, the claim merged an active tier,
  // and the "trial has ended" wall stayed until a manual reload.
  useEffect(() => {
    if (screen === 'paywall' && tenantHasAccess(tenant) && !isTrial) setScreen('app');
  }, [screen, tenant, isTrial]);

  // ─── Auth Functions ─────────────────────────────────────────────────

  const checkAuth = useCallback(async () => {
    const token = getDeviceToken();
    if (!token) {
      // Unauthenticated: trial state only means anything for a real,
      // server-known tenant (tenant.trialEndsAt below). This used to render
      // 'app' straight off a user-writable `${appId}_trial_start`
      // localStorage timestamp with NO token at all — forging that one key
      // by hand (devtools, no server round trip whatsoever) was enough to
      // get the app. There is no local grant path here anymore: no token
      // always means login/registration.
      setIsLoading(false);
      setScreen('login');
      return;
    }

    try {
      const res = await fetch(`${baseUrl}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        clearStoredToken(appId);
        verifiedTenantRef.current = null;
        setPostSignup(null); // a later sign-in must never inherit this sign-up's owner PIN
        setScreen('login');
        setIsLoading(false);
        return;
      }

      const data = await res.json();
      setUser(data.user || null);
      setTenant(data.tenant || null);
      setCachedTier(data.tenant?.subscriptionTier);
      setCachedLocationLimit(data.tenant?.locationLimit);

      // Server-authoritative only: /me's freshly-fetched tenant is the sole
      // thing that may grant 'app' here. There used to be a local fallback
      // grant (re-minting a trial off `isTrial`, itself derived from
      // localStorage-backed state) — that was the hole: clearing
      // `${appId}_trial_start` reset the local clock to "trial not
      // started", which read as eligible even for a tenant the server had
      // just said was unentitled.
      if (tenantHasAccess(data.tenant)) {
        verifiedTenantRef.current = data.tenant;
        setScreen('app');
      } else {
        verifiedTenantRef.current = null;
        setScreen('paywall');
      }
    } catch {
      // Network error: fail CLOSED for any NEW grant — never mint or extend
      // a trial here, that's the same localStorage-resettable hole. Only
      // exception: this session already proved entitlement against the real
      // server once, so a transient blip must not revoke it mid-session.
      if (verifiedTenantRef.current && tenantHasAccess(verifiedTenantRef.current)) {
        setScreen('app');
      } else {
        setPostSignup(null);
        setScreen('login');
      }
    } finally {
      setIsLoading(false);
    }
  }, [appId, baseUrl]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError('');
    try {
      const res = await fetch(`${baseUrl}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Login failed');
        return false;
      }

      setDeviceToken(data.token || data.accessToken);
      setUser(data.user);
      // /login's response doesn't carry trialEndsAt/isGrandfathered — reuse
      // checkAuth's /me fetch so login and a fresh page load compute access
      // through the exact same path.
      await checkAuth();
      return true;
    } catch {
      setError('Network error. Please try again.');
      return false;
    }
  };

  const register = async (email, password, name, restaurantName) => {
    setError('');
    try {
      const res = await fetch(`${baseUrl}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, restaurantName }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Registration failed');
        return false;
      }

      setDeviceToken(data.token || data.accessToken);
      setUser(data.user);
      startTrial(data.token || data.accessToken);
      setPostSignup({ ownerPin: data.starterStaff?.ownerPin || null });
      await checkAuth();
      return true;
    } catch {
      setError('Network error. Please try again.');
      return false;
    }
  };

  const logout = () => {
    clearStoredToken(appId);
    verifiedTenantRef.current = null;
    setUser(null);
    setTenant(null);
    setCachedTier(null);
    setCachedLocationLimit(null);
    setPostSignup(null);
    setScreen('login');
  };

  const subscribe = async (tierId) => {
    // Apple builds must not sell digital subscriptions outside Apple IAP
    // (3.1.1) — native builds render the StoreKit paywall instead and never
    // call this. Kept as a defense-in-depth guard, not the entitlement gate.
    if (isNativeApp) return;
    const token = getDeviceToken();
    try {
      // ticket 11399: checkout lives on the payments router, not authUrl
      // (/api/tenants has no create-checkout-session route).
      const res = await fetch(`${API_BASE_URL}/api/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tier: tierId }),
      });
      const data = await res.json();

      if (data.url) {
        // Open Stripe checkout in default browser (works in Electron)
        if (window.electron?.openExternal) {
          window.electron.openExternal(data.url);
        } else {
          window.open(data.url, '_blank');
        }
      }
    } catch {
      setError('Could not start checkout. Please try again.');
    }
  };

  const isSubscribed = tenantHasAccess(tenant);

  const contextValue = {
    user,
    tenant,
    subscription: tenant ? { active: isSubscribed, tier: tenant.subscriptionTier } : null,
    isLoading,
    isAuthenticated: !!user,
    isSubscribed,
    isTrial,
    trialDaysLeft,
    isPremium: isSubscribed,
    login,
    register,
    logout,
    subscribe,
    purchasePremium,
    restorePremium,
  };

  // ─── Render ─────────────────────────────────────────────────────────

  if (screen === 'loading') {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  if (screen === 'login') {
    return (
      <SubscriptionContext.Provider value={contextValue}>
        <RendererReadyShell>
          <LoginScreen
            appName={pricing?.name || appId}
            error={error}
            onLogin={login}
            onRegister={register}
            trialDays={trialDays}
          />
        </RendererReadyShell>
      </SubscriptionContext.Provider>
    );
  }

  // Post-sign-up steps, then whatever screen checkAuth chose (app or paywall,
  // unchanged). Never shown on login, a page load or a restored session.
  if (postSignup && (screen === 'app' || screen === 'paywall')) {
    return (
      <SubscriptionContext.Provider value={contextValue}>
        <RendererReadyShell>
          <PostSignupSetup ownerPin={postSignup.ownerPin} onDone={() => setPostSignup(null)} />
        </RendererReadyShell>
      </SubscriptionContext.Provider>
    );
  }

  if (screen === 'paywall') {
    return (
      <SubscriptionContext.Provider value={contextValue}>
        <RendererReadyShell>
          <PaywallScreen
            appName={pricing?.name || appId}
            tiers={pricing?.tiers || []}
            onSubscribe={subscribe}
            onLogout={logout}
            isNative={isNativeApp}
            purchasePremium={purchasePremium}
            restorePremium={restorePremium}
            trialDaysLeft={isTrial ? trialDaysLeft : null}
            onClose={isTrial ? () => setScreen('app') : null}
          />
        </RendererReadyShell>
      </SubscriptionContext.Provider>
    );
  }

  // screen === 'app'
  return (
    <SubscriptionContext.Provider value={contextValue}>
      <RendererReadyShell
        top={isTrial && (
          <div style={styles.trialBanner}>
            Trial: {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining
            <button
              onClick={() => setScreen('paywall')}
              style={styles.trialUpgradeBtn}
            >
              Upgrade
            </button>
          </div>
        )}
      >
        {children}
      </RendererReadyShell>
    </SubscriptionContext.Provider>
  );
}

// ─── Sub-Components ─────────────────────────────────────────────────────────

function LoginScreen({ appName, error, onLogin, onRegister, trialDays }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (mode === 'login') {
      await onLogin(email, password);
    } else {
      // server/routes/tenants.js's POST /register 400s without this — the
      // native paywall's registration form has to collect it too, not just
      // the web onboarding wizard (ticket 4556 finding: native sign-up was
      // otherwise unusable).
      await onRegister(email, password, name, restaurantName);
    }
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>{appName}</h1>
        <p style={styles.subtitle}>
          {mode === 'login' ? 'Sign in to continue' : 'Create your account'}
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {mode === 'register' && (
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={styles.input}
              required
            />
          )}
          {mode === 'register' && (
            <input
              type="text"
              placeholder="Restaurant Name"
              value={restaurantName}
              onChange={(e) => setRestaurantName(e.target.value)}
              style={styles.input}
              required
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            required
            minLength={6}
          />

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.primaryBtn} disabled={loading}>
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <button
          onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
          style={styles.linkBtn}
        >
          {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
        </button>

        <div style={styles.divider}>
          <span style={styles.dividerText}>or</span>
        </div>

        <button onClick={() => setMode('register')} style={styles.secondaryBtn}>
          Start {trialDays}-Day Free Trial
        </button>
      </div>
    </div>
  );
}

// trialDaysLeft is null unless the tenant is inside an active trial (the
// paywall was opened voluntarily from the trial banner's Upgrade button);
// only then is "Your trial has ended" false, so it is the one switch.
function paywallSubtitle(trialDaysLeft, cta) {
  if (trialDaysLeft == null) return `Your trial has ended. ${cta} to continue.`;
  return `${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} left in your free trial. ${cta} anytime.`;
}

function PaywallCloseButton({ onClose }) {
  if (!onClose) return null;
  return (
    <button onClick={onClose} style={styles.linkBtn}>
      Not now
    </button>
  );
}

function PaywallScreen({ appName, tiers, onSubscribe, onLogout, isNative, purchasePremium, restorePremium, trialDaysLeft = null, onClose = null }) {
  if (isNative) {
    return (
      <NativePaywallScreen
        appName={appName}
        onLogout={onLogout}
        purchasePremium={purchasePremium}
        restorePremium={restorePremium}
        trialDaysLeft={trialDaysLeft}
        onClose={onClose}
      />
    );
  }

  return (
    <div style={styles.container}>
      <div style={{ ...styles.card, maxWidth: '600px' }}>
        <h1 style={styles.title}>Upgrade {appName}</h1>
        <p style={styles.subtitle}>{paywallSubtitle(trialDaysLeft, 'Choose a plan')}</p>

        <div style={styles.tiersContainer}>
          {tiers.map((tier) => (
            <div
              key={tier.id}
              style={{
                ...styles.tierCard,
                ...(tier.recommended ? styles.tierCardRecommended : {}),
              }}
            >
              {tier.recommended && (
                <span style={styles.recommendedBadge}>Recommended</span>
              )}
              <h3 style={styles.tierName}>{tier.label}</h3>
              <div style={styles.tierPrice}>
                <span style={styles.priceAmount}>
                  ${tier.price.toFixed(2)}
                </span>
                <span style={styles.priceInterval}>/{tier.interval}</span>
              </div>
              <ul style={styles.featureList}>
                {tier.features.map((f, i) => (
                  <li key={i} style={styles.featureItem}>{f}</li>
                ))}
              </ul>
              <button
                onClick={() => onSubscribe(tier.id)}
                style={tier.recommended ? styles.primaryBtn : styles.secondaryBtn}
              >
                Subscribe
              </button>
            </div>
          ))}
        </div>

        <PaywallCloseButton onClose={onClose} />
        <button onClick={onLogout} style={styles.linkBtn}>
          Sign out
        </button>
      </div>
    </div>
  );
}

// Native-only App Store paywall (Guideline 3.1.1: no Stripe tiers, no
// external checkout link, no free bypass). Reviewer notes + demo account:
// APPROVAL_SPEC.md "Reviewer demo account". Mirrors the terpa PaywallView
// conventions NativeUpgradeModal already established for the in-app feature
// gates (explicit price + period, 7-day trial, Restore Purchases, legal
// links, auto-renew disclosure) — inlined here (not imported) to avoid a
// SubscriptionGate <-> NativeUpgradeModal circular import.
function NativePaywallScreen({ appName, onLogout, purchasePremium, restorePremium, trialDaysLeft, onClose }) {
  const [products, setProducts] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyProductId, setBusyProductId] = useState(null);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getAppStoreProducts()
      .then((list) => { if (!cancelled) setProducts(list); })
      .catch((err) => { if (!cancelled) setLoadError(err.message || 'Unable to load App Store plans'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSubscribe = async (productId) => {
    setActionError('');
    setBusyProductId(productId);
    try {
      await purchasePremium(productId);
    } catch (err) {
      setActionError(err.message || 'Purchase failed. Please try again.');
    } finally {
      setBusyProductId(null);
    }
  };

  const handleRestore = async () => {
    setActionError('');
    setBusyProductId('restore');
    try {
      await restorePremium();
    } catch (err) {
      setActionError(err.message || 'No active subscription was found.');
    } finally {
      setBusyProductId(null);
    }
  };

  return (
    <div style={styles.container}>
      <div style={{ ...styles.card, maxWidth: '440px' }}>
        <h1 style={styles.title}>Upgrade {appName}</h1>
        <p style={styles.subtitle}>{paywallSubtitle(trialDaysLeft, 'Subscribe through the App Store')}</p>

        {loading && <p style={styles.loadingText}>Loading plans...</p>}
        {loadError && <p style={styles.error}>{loadError}</p>}

        {!loading && !loadError && (
          <div style={styles.tiersContainer}>
            {products.map((product) => (
              <div key={product.productId} style={styles.tierCard}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={styles.tierName}>{product.displayName}</span>
                  <span style={styles.priceAmount}>
                    {product.formattedPrice}
                    {product.subscriptionPeriod && (
                      <span style={styles.priceInterval}> / {product.subscriptionPeriod}</span>
                    )}
                  </span>
                </div>
                <p style={{ ...styles.featureItem, marginBottom: '0.75rem' }}>{product.description}</p>
                <p style={{ ...styles.featureItem, color: '#4ade80', marginBottom: '0.75rem' }}>Includes a 7-day free trial</p>
                <button
                  onClick={() => handleSubscribe(product.productId)}
                  disabled={busyProductId !== null}
                  style={styles.primaryBtn}
                >
                  {busyProductId === product.productId ? 'Please wait...' : 'Subscribe'}
                </button>
              </div>
            ))}
          </div>
        )}

        {actionError && <p style={styles.error}>{actionError}</p>}

        <button onClick={handleRestore} disabled={busyProductId !== null} style={styles.linkBtn}>
          {busyProductId === 'restore' ? 'Restoring...' : 'Restore Purchases'}
        </button>

        <p style={{ color: '#64748b', fontSize: '0.75rem', textAlign: 'center', margin: '0.5rem 0', lineHeight: 1.5 }}>
          Subscriptions automatically renew unless auto-renew is turned off at least 24 hours
          before the end of the current period. Manage or cancel in your App Store account settings.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
          <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer" style={{ color: '#64748b', fontSize: '0.75rem' }}>Terms of Use</a>
          <a href="https://apps.quantrolix.ai/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#64748b', fontSize: '0.75rem' }}>Privacy Policy</a>
        </div>

        <PaywallCloseButton onClose={onClose} />
        <button onClick={onLogout} style={styles.linkBtn}>
          Sign out
        </button>
      </div>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = {
  // Outside the app shell (nothing rendered yet): fills the window itself.
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0f172a',
    color: '#e2e8f0',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '1rem',
  },
  // Inside the app shell body (login, paywall): fills it, grows and scrolls with content.
  container: {
    minHeight: '100%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0f172a',
    color: '#e2e8f0',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '1rem',
  },
  card: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '2rem',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 700,
    textAlign: 'center',
    margin: '0 0 0.5rem',
    color: '#f1f5f9',
  },
  subtitle: {
    textAlign: 'center',
    color: '#94a3b8',
    margin: '0 0 1.5rem',
    fontSize: '0.95rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  input: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '1px solid #334155',
    background: '#0f172a',
    color: '#e2e8f0',
    fontSize: '1rem',
    outline: 'none',
  },
  primaryBtn: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: 'none',
    background: '#3b82f6',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
    width: '100%',
  },
  secondaryBtn: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: '1px solid #475569',
    background: 'transparent',
    color: '#94a3b8',
    fontSize: '1rem',
    cursor: 'pointer',
    width: '100%',
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#60a5fa',
    fontSize: '0.9rem',
    cursor: 'pointer',
    padding: '0.75rem',
    textAlign: 'center',
    width: '100%',
  },
  error: {
    color: '#f87171',
    fontSize: '0.9rem',
    margin: 0,
    textAlign: 'center',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '1rem 0',
    gap: '0.75rem',
  },
  dividerText: {
    color: '#64748b',
    fontSize: '0.85rem',
    flex: 'none',
  },
  // In normal flow, not position: fixed — fixed at top: 0 it sat on top of
  // every screen's header (iPhone report, 1.0.2). It now pushes the app down.
  trialBanner: {
    position: 'relative',
    flexShrink: 0,
    background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
    color: '#fff',
    textAlign: 'center',
    padding: '8px 16px',
    fontSize: '0.85rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  trialUpgradeBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: '#fff',
    padding: '3px 12px',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  tiersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  tierCard: {
    border: '1px solid #334155',
    borderRadius: '10px',
    padding: '1.25rem',
    position: 'relative',
  },
  tierCardRecommended: {
    border: '2px solid #3b82f6',
    background: 'rgba(59,130,246,0.05)',
  },
  recommendedBadge: {
    position: 'absolute',
    top: '-10px',
    right: '12px',
    background: '#3b82f6',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '2px 10px',
    borderRadius: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tierName: {
    margin: '0 0 0.5rem',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#f1f5f9',
  },
  tierPrice: {
    marginBottom: '0.75rem',
  },
  priceAmount: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#f1f5f9',
  },
  priceInterval: {
    color: '#64748b',
    fontSize: '0.9rem',
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 1rem',
  },
  featureItem: {
    padding: '4px 0',
    fontSize: '0.9rem',
    color: '#94a3b8',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #334155',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#94a3b8',
    marginTop: '1rem',
  },
};
